/**
 * Le seul endroit du front qui parle à `/apparence/*` (SPEC-BOUTIQUE §6).
 *
 * ★ Aucun calcul de couleur ni de contraste ici — et c'est délibéré.
 *
 * PER-05 est un refus opposable : c'est le serveur qui mesure et qui décide
 * (`modules/apparence/couleurs.ts`). Recalculer les mêmes rapports dans le
 * navigateur donnerait deux implémentations d'une même règle d'accessibilité,
 * qui divergeraient un jour — et ce jour-là, l'écran afficherait « conforme »
 * sur une couleur que l'API refuse. L'aperçu (PER-06) interroge donc le même
 * calcul que l'enregistrement.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ModeSalle } from '@tb/shared'

import { api } from '../../lib/api'
import { CLE_THEATRE_PUBLIQUE } from '../../lib/theatre'

/* -------------------------------------------------------------------------- */
/* Types — miroir des schémas de `backend/src/modules/apparence/schemas.ts`    */
/* -------------------------------------------------------------------------- */

/** Les onze tons, indexés par leur numéro (`50`, `100`, … `950`). */
export type Echelle = Readonly<Record<string, string>>

/** Les onze tons, dans l'ordre — sert aux nuanciers et à l'injection. */
export const TONS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const

/** Une mesure de contraste, telle qu'un audit d'accessibilité la citerait. */
export interface Controle {
  readonly cle: string
  readonly libelle: string
  /** Où l'usage se trouve réellement dans le produit. */
  readonly usage: string
  readonly avantPlan: string
  readonly arrierePlan: string
  readonly seuil: number
  readonly ratio: number
  readonly conforme: boolean
}

/** Verdict de PER-05 sur une couleur — c'est ce que l'aperçu affiche. */
export interface Verdict {
  readonly accent: string
  readonly echelle: Echelle
  readonly controles: readonly Controle[]
  readonly conforme: boolean
  /** La variante la plus proche qui passe. Absente si la couleur passe déjà. */
  readonly proposition?: string
}

export interface Police {
  readonly cle: string
  readonly libelle: string
  readonly description: string
  readonly pile: string
  readonly fichier: string | null
}

/**
 * ★ PER-06 / LIB-04 — le billet d'exemple de l'aperçu, pris dans le décor
 * RÉEL du tenant (une vraie représentation) plutôt que dans une constante.
 * `null` quand le tenant n'a encore rien programmé — l'aperçu retombe alors
 * sur une phrase neutre plutôt que d'inventer un spectacle.
 */
export interface ExempleDecor {
  readonly titre: string
  readonly dateHeure: string
  readonly salleMode: ModeSalle
  /** Le prix « plein tarif » réel de ce spectacle, en centimes. `null` si aucun n'est configuré. */
  readonly montantExempleCentimes: number | null
}

export interface ApparenceAdmin {
  readonly couleurAccent: string
  readonly police: string
  readonly nomAffiche: string | null
  readonly mentionsPied: string | null
  readonly logoPath: string | null
  readonly accroche: string | null
  readonly texteBillet: string | null
  readonly texteCarte: string | null
  readonly nomEffectif: string
  readonly raisonSociale: string
  readonly echelle: Echelle
  readonly controles: readonly Controle[]
  readonly conforme: boolean
  readonly polices: readonly Police[]
  readonly pilePolice: string
  readonly exemple: ExempleDecor | null
}

export interface MajApparence {
  readonly couleurAccent: string
  readonly police: string
  readonly nomAffiche?: string | null
  readonly mentionsPied?: string | null
  readonly accroche?: string | null
  /** PER-05 — « oui, prends la variante que tu proposes ». */
  readonly corriger?: boolean
}

/** PER-07 — ce que reçoit un visiteur qui n'a pas de session. */
export interface ApparencePublique {
  readonly nom: string
  readonly logo: string | null
  readonly couleurAccent: string
  readonly echelle: Echelle
  /** Valeur prête à poser dans `--font-sans`. */
  readonly police: string
  readonly policeFichier: string | null
  readonly accroche: string | null
  readonly mentionsPied: string | null
}

/* -------------------------------------------------------------------------- */
/* Clés de cache                                                              */
/* -------------------------------------------------------------------------- */

export const CLES = {
  publique: ['apparence', 'publique'] as const,
  admin: ['apparence', 'admin'] as const,
}

/* -------------------------------------------------------------------------- */
/* Hooks                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * L'apparence publique. Chargée une fois au démarrage de l'application, avant
 * toute session : c'est elle qui peint la page de connexion et la boutique.
 *
 * `retry: false` et aucune erreur propagée : une API muette doit laisser
 * l'interface s'afficher avec ses couleurs d'origine, pas la remplacer par un
 * écran d'erreur. Une personnalisation absente n'est pas une panne.
 */
export function useApparencePublique() {
  return useQuery({
    queryKey: CLES.publique,
    queryFn: () => api.get<ApparencePublique>('/apparence/publique'),
    /* Un théâtre change de couleur une fois par décennie. */
    staleTime: 30 * 60_000,
    retry: false,
  })
}

export function useApparenceAdmin() {
  return useQuery({
    queryKey: CLES.admin,
    queryFn: () => api.get<ApparenceAdmin>('/apparence'),
  })
}

export function useMajApparence() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (corps: MajApparence) => api.put<ApparenceAdmin>('/apparence', corps),
    onSuccess: async (vue) => {
      client.setQueryData(CLES.admin, vue)
      /* La barre latérale, la boutique et la page de connexion lisent la vue
         PUBLIQUE : sans cette invalidation, le nouvel accent n'apparaîtrait
         qu'au prochain rechargement complet du navigateur. */
      await client.invalidateQueries({ queryKey: CLES.publique })
      /* `nomAffiche` (PER-01) décide aussi du nom rendu par
         `GET /theatre/publique`, que lisent les en-têtes des trois espaces. */
      await client.invalidateQueries({ queryKey: CLE_THEATRE_PUBLIQUE })
    },
  })
}

export function useReinitialiserApparence() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<ApparenceAdmin>('/apparence/reinitialiser'),
    onSuccess: async (vue) => {
      client.setQueryData(CLES.admin, vue)
      await client.invalidateQueries({ queryKey: CLES.publique })
      /* `nomAffiche` (PER-01) décide aussi du nom rendu par
         `GET /theatre/publique`, que lisent les en-têtes des trois espaces. */
      await client.invalidateQueries({ queryKey: CLE_THEATRE_PUBLIQUE })
    },
  })
}

/**
 * PER-06 — l'aperçu. N'écrit rien ; renvoie exactement le verdict que
 * l'enregistrement appliquerait, refus et contre-proposition compris.
 */
export function useApercuApparence() {
  return useMutation({
    mutationFn: (couleurAccent: string) =>
      api.post<Verdict>('/apparence/apercu', { couleurAccent }),
  })
}

/* -------------------------------------------------------------------------- */
/* PER-02 — le logo                                                           */
/* -------------------------------------------------------------------------- */

/**
 * ★ Le logo passe par les routes de CFG-01, `POST/DELETE
 * /configuration/theatre/logo`, et non par une route propre à ce module.
 *
 * Elles existent déjà, avec leurs contrôles (formats acceptés, taille limitée,
 * nom de fichier remplacé par un uuid, ancien fichier effacé après coup). En
 * ouvrir un second chemin d'écriture vers `theatre.logo_path` donnerait deux
 * validations à maintenir et, tôt ou tard, deux comportements. Le logo est
 * édité ICI parce que c'est l'écran où l'on regarde le résultat ; il reste
 * stocké là où il a toujours été.
 */
function invaliderApparence(client: ReturnType<typeof useQueryClient>): Promise<void> {
  return Promise.all([
    client.invalidateQueries({ queryKey: CLES.admin }),
    client.invalidateQueries({ queryKey: CLES.publique }),
    /* L'en-tête du back-office lit encore `GET /configuration/theatre`. */
    client.invalidateQueries({ queryKey: ['configuration', 'theatre'] }),
  ]).then(() => undefined)
}

/** Formats acceptés par le backend (`configuration/storage.ts`). */
export const TYPES_LOGO_ACCEPTES = 'image/png,image/jpeg,image/webp,image/svg+xml'
/** Limite posée par `@fastify/multipart` dans `app.ts`. */
export const TAILLE_MAX_LOGO = 5 * 1024 * 1024

export function useTeleverserLogo() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (fichier: File) => {
      const corps = new FormData()
      corps.append('logo', fichier)
      return api.post<unknown>('/configuration/theatre/logo', corps)
    },
    onSuccess: () => invaliderApparence(client),
  })
}

export function useSupprimerLogo() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => api.del<unknown>('/configuration/theatre/logo'),
    onSuccess: () => invaliderApparence(client),
  })
}
